import React from 'react';
import classNames from 'classnames';
import { Container } from 'src/components/layouts/Container/Container';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import { useTranslation } from 'react-i18next';
import { convertKpiToHeaderItemProps } from 'src/components/layouts/KpiRow.tsx/KpiRow';
import styles from './styles/TrackingAuthForm.module.scss';
import IntegrationsListCard from '../components/cardLayouts/IntegrationsListCard/IntegrationsListCard';
import { DashboardKpi, useDashboardKpiQuery, useKpiQuery } from '../services/kpi';
import { KeyValueListWithDiagramKpiCard } from '../components/cardLayouts/KeyValueListWithDiagramKpiCard/KeyValueListWithDiagramKpiCard';
import { DashboardKeyValueListKpiCard } from '../components/cardLayouts/DashboardKeyValueListKpiCard/DashboardKeyValueListKpiCard';
import DashboardCircleProgressDiagramKpiCard from '../components/cardLayouts/DashboardCircleProgressDiagramKpiCard/DashboardCircleProgressDiagramKpiCard';
import DashboardAvgCalcWithProgressKpiCard from '../components/cardLayouts/DashboardAvgCalcWithProgressKpiCard/DashboardAvgCalcWithProgressKpiCard';
import DashboardTimeSeriesDiagramKpiCard from '../components/cardLayouts/DashboardTimeSeriesDiagramKpiCard/DashboardTimeSeriesDiagramKpiCard';
import DashboardKeyValueRankingKpiCard from '../components/cardLayouts/DashboardKeyValueRankingKpiCard/DashboardKeyValueRankingKpiCard';
import StatisticRow from '../components/cardLayouts/StatisticRow/StatisticRow';
import DashboardBarChartDiagramKpiCard from '../components/cardLayouts/DashboardBarChartDiagramKpiCard/DashboardBarChartDiagramKpiCard';
import DashboardValueWithoutProgressKpiCard from '../components/cardLayouts/DashboardValueWithoutProgressKpiCard/DashboardValueWithoutProgressKpiCard';
import { useNavigationItems } from '../hooks/useNavigationItems';

function IntegrationsDashboard() {
  const { t } = useTranslation();
  const { data } = useDashboardKpiQuery('integrations.dashboard');
  const { findAccessRights } = useNavigationItems();
  const connectorAccessRights = findAccessRights('integrations/connectors');
  const kpis = data as DashboardKpi[] | undefined;
  const { data: topKpis } = useKpiQuery('df.channel.connector');

  const withoutProgressKpis = kpis?.filter(kpi => kpi.kpi_type === 'dashboard_value_without_progress');
  const otherKpis = kpis?.filter(kpi => kpi.kpi_type !== 'dashboard_value_without_progress');

  return (
    <Container
      title={`${t('integrations.integrations')} / ${t('integrations.dashboard')}`}
      className={classNames(styles.root, 'relative py-32 flex-1')}>
      <HeadlineWithButtons headline={t('integrations.dashboard')} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-20 mb-20">
        {Array.isArray(topKpis) && (
          <div className="col-span-full">
            <div>
              <StatisticRow items={convertKpiToHeaderItemProps(topKpis, t)} />
            </div>
          </div>
        )}
        {otherKpis?.map((kpi, idx) => {
          if (kpi.kpi_type === 'key_value_list_with_diagram') {
            return (
              <div key={idx}>
                <div className="h-full">
                  <KeyValueListWithDiagramKpiCard data={kpi} />
                </div>
              </div>
            );
          }
          if (kpi.kpi_type === 'dashboard_key_value_list') {
            return (
              <div key={idx}>
                <div className="h-full">
                  <DashboardKeyValueListKpiCard data={kpi} />
                </div>
              </div>
            );
          }
          if (kpi.kpi_type === 'dashboard_circle_progress_diagram') {
            return (
              <div key={idx}>
                <div>
                  <DashboardCircleProgressDiagramKpiCard data={kpi} />
                </div>
              </div>
            );
          }
          if (kpi.kpi_type === 'dashboard_avg_calc_with_progress') {
            return (
              <div key={idx}>
                <div>
                  <DashboardAvgCalcWithProgressKpiCard data={kpi} />
                </div>
              </div>
            );
          }
          if (kpi.kpi_type === 'dashboard_time_series_diagram') {
            return (
              <div key={idx} className="col-span-full">
                <div>
                  <DashboardTimeSeriesDiagramKpiCard data={kpi} />
                </div>
              </div>
            );
          }
          if (kpi.kpi_type === 'dashboard_key_value_ranking') {
            return (
              <div key={idx}>
                <div className="h-full">
                  <DashboardKeyValueRankingKpiCard data={kpi} />
                </div>
              </div>
            );
          }
          if (kpi.kpi_type === 'dashboard_bar_chart_diagram') {
            return (
              <div key={idx} className="col-span-full">
                <div>
                  <DashboardBarChartDiagramKpiCard title={kpi.title} data={kpi} />
                </div>
              </div>
            );
          }
          return null;
        })}
        {connectorAccessRights && (
          <div className="grid grid-cols-1 md:grid-cols-1 w-full gap-[20px]">
            <IntegrationsListCard />
          </div>
        )}
        {withoutProgressKpis && withoutProgressKpis.length > 0 && (
          <div className="col-span-1">
            <DashboardValueWithoutProgressKpiCard data={withoutProgressKpis} />
          </div>
        )}
      </div>
    </Container>
  );
}

export default IntegrationsDashboard;
